import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { S, PNL_CATEGORIES, todayStr } from "../../crm/theme";
import PnlSettings from "./PnlSettings";
import DateBox from "../../crm/DateBox";
import { fmtD, fmtDT } from "../../crm/dateFormat";
import PnlForecastTab from "./PnlForecastTab";
import {
  fetchLeads, fetchPnlEntriesV2, createPnlEntryV2, createPnlEntriesV2, updatePnlEntryV2, tagOldExpensesOwner,
  fetchPnlSettings, COMMON_PROJECT,
  visibleTo, canSeeAll, leadRevenue, wonRevenue,
} from "../../crm/crmApi";
import {
  BANK_MODES, PAID_FROM_BANK, PAID_FROM_OWNER, PAID_FROM_CREDIT, GOOGLE_CATEGORY, TOPUP_TYPE, TOPUP_CATEGORY, todayNumbers,
  buildEntries, buildReversal, googleToBook, googleEntry, splitGst, projectSummary,
  editRules, buildEdit, describeChanges, editLogList,
} from "../../crm/pnlEntry";
import { fetchGoogleCostsInr } from "../../crm/googleCost";
import { db } from "../../firebase/config";
import { collection as fsCollection, getDocs as fsGetDocs, doc as fsDoc, getDoc as fsGetDoc } from "firebase/firestore";

// Shows paise when there are any (GST often has them): 847.46 stays 847.46, 1000 stays 1,000.
const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const blankEntry = () => ({
  date: todayStr(), description: "", amount: "", gstRate: "", gstIncluded: "no",
  type: "Income", category: "Subscription", direction: "In", paymentMode: "UPI", party: "",
  project: COMMON_PROJECT, paidFrom: PAID_FROM_BANK, tdsAmount: "", gcRows: {},
});
const PIE_COLORS = ["#6c63ff", "#4f8ef7", "#3ecf8e", "#f0b429", "#e879f9", "#f59e42", "#f87171", "#7b82a0"];
const MONTH_NAMES = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const INCOME_CATEGORIES = ["Subscription", "Onboarding fee", "Other income"];
const EXPENSE_CATEGORIES = [...PNL_CATEGORIES.filter((c) => c !== "Other" && c !== GOOGLE_CATEGORY), GOOGLE_CATEGORY, "Bank charges", "Other"];
const categoriesFor = (type) => (type === "Income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES);
// Categories an existing entry may be changed to. Google Cloud has its own bill form, so it is not offered here;
// the entry's own category stays in the list so an old name never forces a change.
const editCategoriesFor = (e) => {
  const base = categoriesFor(e.type).filter((c) => c !== GOOGLE_CATEGORY);
  return e.category && !base.includes(e.category) ? [...base, e.category] : base;
};
const typeLabelOf = (e) => (e.type === "Transfer" ? (e.category === TOPUP_CATEGORY ? "Google credit top-up" : e.direction === "Out" ? "Owner's withdrawal" : "Owner's money") : e.type);
const TYPE_FILTERS = ["Income", "Expense", "Owner's money", "Owner's withdrawal", "Google credit top-up"];
const NO_PROJECT = "__none";
const whenText = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso || "") : fmtDT(d);
};

// Indian financial year runs April to March. FY "2026" means Apr 2026 - Mar 2027.
const fyOf = (dateStr) => {
  const y = Number(dateStr.slice(0, 4));
  const m = Number(dateStr.slice(5, 7));
  return m >= 4 ? y : y - 1;
};
const fyLabel = (fy) => `${fy}-${String(fy + 1).slice(2)}`;
const money = (v) => Number(v) || 0;

// Only the two Owners may see business income and expenses. The database rule
// (firestore.rules, crmPnl) enforces the same list, so this screen only mirrors it.
const PNL_OWNER_EMAILS = ["layakarudu@gmail.com", "dr.vijaydsk@gmail.com"];

export default function CrmPnLScreen({ user }) {
  const [leads, setLeads] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState(blankEntry());
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("today"); // "today", "projects", "entries", "pnl" or "settings"
  const [fProject, setFProject] = useState("all");  // Entries tab filters
  const [fType, setFType] = useState("all");
  const [fText, setFText] = useState("");
  const [editing, setEditing] = useState(null);       // the entry being edited: { id, description, party, project, category }
  const [editSaving, setEditSaving] = useState(false);
  const [openLog, setOpenLog] = useState(null);       // id of the entry whose change record is open
  const [settings, setSettings] = useState(null);
  const [gBook, setGBook] = useState(null);       // preview of Google cost to book, or null
  const [gPaidFrom, setGPaidFrom] = useState(PAID_FROM_BANK);
  const [gBusy, setGBusy] = useState(false);
  const [gAuto, setGAuto] = useState("");           // what the automatic Google check did
  const autoRan = useRef(false);                     // the automatic check runs once per opening of the screen

  const nowFy = fyOf(todayStr());
  const [fy, setFy] = useState(String(nowFy)); // "all" or a year like "2026"
  const [month, setMonth] = useState("all");   // "all" or "04".."03"

  const canSeeBusinessPnl = PNL_OWNER_EMAILS.includes((user?.email || "").trim().toLowerCase());
  const seesAllLeads = canSeeAll(user);

  const load = async () => {
    setLoading(true);
    try {
      const [allLeads, allEntries, st] = await Promise.all([
        fetchLeads(),
        canSeeBusinessPnl ? fetchPnlEntriesV2() : Promise.resolve([]),
        canSeeBusinessPnl ? fetchPnlSettings() : Promise.resolve(null),
      ]);
      setLeads(visibleTo(allLeads, user));
      setEntries(allEntries);
      setSettings(st);
      if (canSeeBusinessPnl && st) runAutoGoogle(st);
    } catch (e) {
      setMsg("Error loading P&L: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  // Automatic Google cost: when the screen opens, ask Google for the cost so far and book only what is new,
  // from the prepaid credit. If the new cost is more than the credit left, nothing is booked and the
  // Owner is asked where the extra was paid from (the panel below opens). Entries are re-read first,
  // so the same cost can never be booked twice.
  const runAutoGoogle = async (st) => {
    if (autoRan.current) return;
    autoRan.current = true;
    const lk = st.googleLinks || [];
    if (lk.length === 0) return;
    const r2 = (n) => Math.round(n * 100) / 100;
    try {
      const { costs, rate } = await fetchGoogleCostsInr(lk.map((l) => l.googleProject));
      const fresh = await fetchPnlEntriesV2();
      const rows = googleToBook(lk, costs, fresh);
      const todo = rows.filter((r) => r.known && r.toBook >= 0.01);
      const stamp = fmtDT(new Date());
      if (todo.length === 0) { setGAuto(`Checked with Google automatically on ${stamp}. Nothing new to book.`); return; }
      const total = r2(todo.reduce((s, r) => s + r.toBook, 0));
      const credit = todayNumbers(fresh).credit;
      if (total <= credit) {
        await createPnlEntriesV2(todo.map((r) => googleEntry(r, PAID_FROM_CREDIT, todayStr())), user);
        setEntries(await fetchPnlEntriesV2());
        setGAuto(`Checked with Google automatically on ${stamp}. Booked ${inr(total)} from your Google credit.`);
      } else {
        setGBook({ rows, rate });
        setGPaidFrom(PAID_FROM_BANK);
        setGAuto(`New Google cost is ${inr(total)} but only ${inr(credit)} of credit is left. Nothing was booked. Choose below where the money was paid from.`);
      }
    } catch (e) {
      setGAuto("The automatic Google cost check did not work: " + e.message);
    }
  };

  // Messages such as "Entry added." clear themselves after a few seconds.
  useEffect(() => {
    if (!msg) return undefined;
    const id = setTimeout(() => setMsg(""), 6000);
    return () => clearTimeout(id);
  }, [msg]);

  const visibleProjects = useMemo(
    () => (settings ? settings.projects.filter((p) => !p.hidden).map((p) => p.name) : [COMMON_PROJECT]),
    [settings]
  );
  const links = settings ? settings.googleLinks : [];
  // Old copied expenses that still need their Paid-from tag (one time).
  const untagged = useMemo(
    () => entries.filter((e) => e.source === "migrated" && e.type === "Expense" && !e.paidFrom),
    [entries]
  );

  // ---- Won deals: shown separately, NOT counted as income until money is received ----
  const wonLeads = useMemo(() => leads.filter((l) => l.stage === "Won"), [leads]);
  const myWonRevenue = useMemo(() => wonRevenue(leads), [leads]);

  // ---- Which original entries have been cancelled ----
  const cancelledIds = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => { if (e.reversalOf) s.add(e.reversalOf); });
    return s;
  }, [entries]);

  // ---- Financial year list for the dropdown ----
  const fyOptions = useMemo(() => {
    const set = new Set([nowFy]);
    entries.forEach((e) => { if (e.date) set.add(fyOf(e.date)); });
    return Array.from(set).sort((a, b) => b - a);
  }, [entries, nowFy]);

  // ---- Entries inside the chosen year / month ----
  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (!e.date) return false;
      if (fy !== "all" && fyOf(e.date) !== Number(fy)) return false;
      if (month !== "all" && e.date.slice(5, 7) !== month) return false;
      return true;
    });
  }, [entries, fy, month]);

  // ---- Entries tab: the year / month above, plus Project, Type and a search box ----
  const projectChoices = useMemo(() => {
    const set = new Set(visibleProjects);
    entries.forEach((e) => { if (e.project) set.add(e.project); });
    return Array.from(set);
  }, [entries, visibleProjects]);
  const shown = useMemo(() => {
    const q = fText.trim().toLowerCase();
    return filtered.filter((e) => {
      if (fProject === NO_PROJECT ? !!e.project : fProject !== "all" && e.project !== fProject) return false;
      if (fType !== "all" && typeLabelOf(e) !== fType) return false;
      if (q && ![e.description, e.party, e.project, e.category].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [filtered, fProject, fType, fText]);
  const moreFilters = fProject !== "all" || fType !== "all" || fText.trim() !== "";
  const shownSum = (type, field = "amount") => shown.filter((e) => e.type === type).reduce((s, e) => s + money(e[field]), 0);
  const shownIncome = { amount: shownSum("Income"), gst: shownSum("Income", "gstAmount") };
  const shownExpense = { amount: shownSum("Expense"), gst: shownSum("Expense", "gstAmount") };

  // Reversal entries carry a negative amount, so plain addition gives the right totals.
  const sumOf = (type, field = "amount") =>
    filtered.filter((e) => e.type === type).reduce((s, e) => s + money(e[field]), 0);

  const totalIncome = sumOf("Income");
  const totalExpense = sumOf("Expense");
  // Owner's money: put in counts plus, taken out counts minus (reversals carry a negative amount).
  const ownerMoney = filtered
    .filter((e) => e.type === "Transfer" && e.category !== TOPUP_CATEGORY)
    .reduce((s, e) => s + money(e.amount) * (e.direction === "Out" ? -1 : 1), 0);

  // Money received from each customer so far (all time, excluding GST, after cancellations).
  const receivedByParty = useMemo(() => {
    const map = {};
    entries.filter((e) => e.type === "Income" && e.party).forEach((e) => {
      const k = e.party.trim().toLowerCase();
      map[k] = (map[k] || 0) + money(e.amount);
    });
    return map;
  }, [entries]);
  const netProfit = totalIncome - totalExpense;
  const gstCollected = sumOf("Income", "gstAmount");
  const gstPaid = sumOf("Expense", "gstAmount");

  // ---- Trend chart ----
  const monthlyTrend = useMemo(() => {
    const months = [];
    if (fy !== "all") {
      for (let i = 0; i < 12; i++) {
        const calMonth = ((3 + i) % 12) + 1;
        const year = calMonth >= 4 ? Number(fy) : Number(fy) + 1;
        months.push({ key: `${year}-${String(calMonth).padStart(2, "0")}`, label: MONTH_NAMES[i], income: 0, expense: 0 });
      }
    } else {
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
          key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
          label: d.toLocaleDateString("en-IN", { month: "short" }), income: 0, expense: 0,
        });
      }
    }
    entries.forEach((e) => {
      if (e.type !== "Income" && e.type !== "Expense") return;
      const m = months.find((mo) => mo.key === (e.date || "").slice(0, 7));
      if (m) m[e.type === "Income" ? "income" : "expense"] += money(e.amount);
    });
    return months;
  }, [entries, fy]);

  const expenseByCategory = useMemo(() => {
    const map = {};
    filtered.filter((e) => e.type === "Expense").forEach((e) => {
      map[e.category] = (map[e.category] || 0) + money(e.amount);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).filter((x) => x.value > 0);
  }, [filtered]);

  // ---- Add entry ----
  const handleAdd = async () => {
    const r = buildEntries(form, links, visibleProjects);
    if (r.error) { setMsg(r.error); return; }
    setSaving(true);
    try {
      if (r.entries.length === 1) await createPnlEntryV2(r.entries[0], user);
      else await createPnlEntriesV2(r.entries, user);
      setForm({ ...blankEntry(), project: form.project });
      setMsg(r.entries.length === 1 ? "Entry added." : `${r.entries.length} entries added (one for each Google project).`);
      await load();
    } catch (e) {
      setMsg("Save failed: " + e.message);
    }
    setSaving(false);
  };

  // ---- Cancel entry: nothing is erased, an opposite entry is added instead ----
  const handleCancel = async (e) => {
    if (!window.confirm("Cancel this entry? The original stays on record and an opposite entry is added.")) return;
    try {
      await createPnlEntryV2(buildReversal(e, todayStr()), user);
      if (editing && editing.id === e.id) setEditing(null);
      setMsg("Entry cancelled.");
      await load();
    } catch (err) {
      setMsg("Cancel failed: " + err.message);
    }
  };

  // ---- Edit entry: only description, customer / vendor, project and category. Every change is recorded. ----
  const handleEditStart = (e) => {
    setEditing({ id: e.id, description: e.description || "", party: e.party || "", project: e.project || "", category: e.category || "" });
  };
  const handleEditSave = async () => {
    const orig = entries.find((x) => x.id === editing.id);
    if (!orig) { setMsg("Entry not found. Please reload."); return; }
    const r = buildEdit(orig, editing, {
      cancelledIds, visibleProjects, categories: editCategoriesFor(orig),
      by: user?.email || "", now: new Date().toISOString(),
    });
    if (r.error) { setMsg(r.error); return; }
    setEditSaving(true);
    try {
      await updatePnlEntryV2(orig.id, r.update);
      setEditing(null);
      setMsg("Entry changed. The change is recorded.");
      await load();
    } catch (err) {
      setMsg("Edit failed: " + err.message);
    }
    setEditSaving(false);
  };

  // ---- One time: tag the old copied expenses as paid from the Owner's pocket ----
  const handleTag = async () => {
    if (!window.confirm(`Mark ${untagged.length} old expense entries as paid from the Owner's pocket?`)) return;
    try {
      const n = await tagOldExpensesOwner();
      setMsg(`${n} old expense entries marked as paid from the Owner's pocket.`);
      await load();
    } catch (err) {
      setMsg("Tagging failed: " + err.message);
    }
  };

  // ---- Google Cloud cost from Google itself: show what is new, then book it ----
  const handleGoogleLook = async () => {
    setGBusy(true);
    try {
      const { costs, rate } = await fetchGoogleCostsInr(links.map((l) => l.googleProject));
      setGBook({ rows: googleToBook(links, costs, entries), rate });
    } catch (err) {
      setMsg("Google cost: " + err.message);
    }
    setGBusy(false);
  };
  const handleGoogleBook = async () => {
    const todo = gBook.rows.filter((r) => r.known && r.toBook >= 0.01);
    if (todo.length === 0) { setMsg("Nothing new to book."); setGBook(null); return; }
    setGBusy(true);
    try {
      await createPnlEntriesV2(todo.map((r) => googleEntry(r, gPaidFrom, todayStr())), user);
      setMsg(`Google cost booked for ${todo.length} project${todo.length === 1 ? "" : "s"}.`);
      setGBook(null);
      await load();
    } catch (err) {
      setMsg("Booking failed: " + err.message);
    }
    setGBusy(false);
  };

  // ---- Download for the accountant (opens in Excel) ----
  // list = the entries to put in the file (the year / month list, or the Entries tab list with its filters).
  const handleDownload = (list, filtersOn) => {
    const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Date", "Description", "Type", "Category", "Project", "Party", "Payment mode", "Paid from", "Amount", "GST", "TDS", "Total", "Note", "Change record"]];
    [...list].sort((a, b) => (a.date || "").localeCompare(b.date || "")).forEach((e) => {
      const log = editLogList(e);
      const note = e.reversalOf ? "Reversal" : cancelledIds.has(e.id) ? "Cancelled" : log.length ? "Edited" : "";
      const record = log.map((l) => `${whenText(l.at)} ${l.by || ""} - ${describeChanges(l.changes)}`).join(" | ");
      rows.push([e.date, e.description, typeLabelOf(e), e.category, e.project || "", e.party || "", e.paymentMode || "", paidLabel(e), money(e.amount), money(e.gstAmount), money(e.tdsAmount), money(e.amount) + money(e.gstAmount), note, record]);
    });
    const csv = "\uFEFF" + rows.map((r) => r.map(q).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `pnl-${fy === "all" ? "all-time" : "FY" + fyLabel(Number(fy))}${month === "all" ? "" : "-m" + month}${filtersOn ? "-filtered" : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isGoogleForm = form.type === "Expense" && form.category === GOOGLE_CATEGORY;
  const isTopup = form.type === TOPUP_TYPE;
  const noGst = form.type === "Transfer" || isTopup;   // no GST, category or project on these
  const showMode = form.type === "Expense" || isTopup ? form.paidFrom === PAID_FROM_BANK : true;
  const today = useMemo(() => todayNumbers(entries), [entries]);
  const projectRows = useMemo(() => projectSummary(filtered, visibleProjects), [filtered, visibleProjects]);

  // ---- Societies tab: one row per society, read from what Setup Wizard saved ----
  const [socRows, setSocRows] = useState(null);   // null = not loaded yet
  const [socErr, setSocErr] = useState("");
  const [priceTiers, setPriceTiers] = useState([]);
  const loadSocieties = async () => {
    setSocErr("");
    setSocRows(null);
    try {
      let tiers = [];
      try {
        const ps = await fsGetDoc(fsDoc(db, "config", "pricing"));
        const pd = ps.exists() ? (ps.data() || {}) : {};
        tiers = Array.isArray(pd.tiers) ? pd.tiers : [];
      } catch (e) { /* the renewal price then shows as a dash */ }
      setPriceTiers(tiers);
      const snap = await fsGetDocs(fsCollection(db, "societies"));
      const rows = await Promise.all(snap.docs.map(async (d) => {
        const s = d.data() || {};
        let init = {};
        try {
          const i = await fsGetDoc(fsDoc(db, "societies", d.id, "settings", "init"));
          init = i.exists() ? (i.data() || {}) : {};
        } catch (e) { /* the row still shows, with blanks */ }
        return {
          id: d.id,
          name: s.name || init.societyName || "",
          status: s.status || "",
          trialEndsAt: s.trialEndsAt || null,
          startIso: s.subscriptionStartDate || null,
          endIso: s.subscriptionEndDate || null,
          flats: init.totalFlats == null ? null : Number(init.totalFlats),
          pricing: init.pricing || null,
        };
      }));
      rows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      setSocRows(rows);
    } catch (e) {
      setSocErr("Could not read the societies: " + e.message);
      setSocRows([]);
    }
  };
  useEffect(() => {
    if (tab === "societies" && canSeeBusinessPnl && socRows === null && !socErr) loadSocieties();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Status and days left come from the saved dates: Trial until the trial ends, then Active until the renewal date.
  const societyView = useMemo(() => {
    const DAY = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const at = (iso) => { const t = iso ? new Date(iso).getTime() : NaN; return Number.isNaN(t) ? null : t; };
    const rows = (socRows || []).map((r) => {
      const trialEnd = at(r.trialEndsAt);
      const end = at(r.endIso);
      let status = "—", tone = "grey", days = null;
      if (r.status === "draft") { status = "Setup not finished"; tone = "grey"; }
      else if (trialEnd !== null && now <= trialEnd) { status = "Trial"; tone = "blue"; days = Math.ceil((trialEnd - now) / DAY); }
      else if (end !== null && now <= end) { status = "Active"; tone = "green"; days = Math.ceil((end - now) / DAY); }
      else if (end !== null) { status = "Renewal overdue"; tone = "amber"; days = -Math.ceil((now - end) / DAY); }
      else if (r.status) { status = r.status; }
      const p = r.pricing;
      const yearly = p && p.type === "tier" && Number(p.priceMonthly) > 0 ? Number(p.priceMonthly) * 12 : null;
      const flatsN = Number(r.flats);
      const tier = r.flats != null && Number.isFinite(flatsN)
        ? priceTiers.find((t) => flatsN >= Number(t.minFlats) && flatsN <= Number(t.maxFlats)) : null;
      const monthly = p && p.type === "tier" && Number(p.priceMonthly) > 0 ? Number(p.priceMonthly) : null;
      const renewal = tier && Number(tier.priceMonthly) > 0 ? Number(tier.priceMonthly) * 12 : null;
      const change = renewal !== null && yearly !== null ? renewal - yearly : null;
      return { ...r, statusText: status, tone, days, yearly, monthly, renewal, change, custom: !!(p && p.type === "custom") };
    });
    const count = (t) => rows.filter((r) => r.statusText === t).length;
    return {
      rows,
      trial: count("Trial"), active: count("Active"), overdue: count("Renewal overdue"),
      yearlyTotal: rows.reduce((s, r) => s + (r.yearly || 0), 0),
      rising: rows.filter((r) => r.change > 0).length,
      risingTotal: rows.reduce((s, r) => s + (r.change > 0 ? r.change : 0), 0),
    };
  }, [socRows, priceTiers]);
  const chip = (tone) => {
    const m = { green: ["#1f3a2e", "#3ecf8e"], blue: ["#1e2a4a", "#4f8ef7"], amber: ["#3a2e14", "#f0b429"], grey: ["#2a2d3e", "#9aa0b8"] }[tone];
    return { background: m[0], color: m[1], padding: "2px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };
  };
  const paidLabel = (e) => (e.paidFrom === PAID_FROM_OWNER ? "Owner's pocket" : e.paidFrom === PAID_FROM_BANK ? "Bank" : e.paidFrom === PAID_FROM_CREDIT ? "Google credit" : "");
  const periodText = fy === "all" ? "All time" : `FY ${fyLabel(Number(fy))}`;
  const monthOrder = ["04", "05", "06", "07", "08", "09", "10", "11", "12", "01", "02", "03"];

  // The edit box opens right under the entry being edited.
  const editPanel = (orig) => {
            const rule = editRules(orig, cancelledIds);
            if (!rule.ok) return null;
            const projectList = orig.project && !visibleProjects.includes(orig.project) ? [...visibleProjects, orig.project] : visibleProjects;
            return (
              <div style={{ ...S.form, borderColor: "#6c63ff", marginBottom: 0 }}>
                <div style={{ fontSize: 13, marginBottom: 12 }}>
                  Edit entry — {fmtD(orig.date)} · {typeLabelOf(orig)} · {inr(money(orig.amount) + money(orig.gstAmount))}
                </div>
                <div style={S.formGrid}>
                  <div>
                    <label style={S.label}>Description</label>
                    <input style={S.input} value={editing.description} onChange={(ev) => setEditing({ ...editing, description: ev.target.value })} />
                  </div>
                  <div>
                    <label style={S.label}>Customer / Vendor</label>
                    <input style={S.input} value={editing.party} onChange={(ev) => setEditing({ ...editing, party: ev.target.value })} />
                  </div>
                  <div>
                    <label style={S.label}>Project</label>
                    {rule.project ? (
                      <select style={S.select} value={editing.project} onChange={(ev) => setEditing({ ...editing, project: ev.target.value })}>
                        {projectList.map((n) => <option key={n}>{n}</option>)}
                      </select>
                    ) : (
                      <div style={{ ...S.input, color: "#7b82a0" }}>{orig.project || "—"} (fixed)</div>
                    )}
                  </div>
                  <div>
                    <label style={S.label}>Category</label>
                    {rule.category ? (
                      <select style={S.select} value={editing.category} onChange={(ev) => setEditing({ ...editing, category: ev.target.value })}>
                        {editCategoriesFor(orig).map((c) => <option key={c}>{c}</option>)}
                      </select>
                    ) : (
                      <div style={{ ...S.input, color: "#7b82a0" }}>{orig.category || "—"} (fixed)</div>
                    )}
                  </div>
                </div>
                <div style={{ color: "#7b82a0", fontSize: 12, marginBottom: 12 }}>
                  Amount, date, GST, TDS, payment mode and Paid from cannot be changed here. If one of them is wrong, cancel this entry and add it again. Every change is recorded with who changed it, when, and what it was before.
                </div>
                {msg && <div style={S.msg}>{msg}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.btnBlue} onClick={handleEditSave} disabled={editSaving}>{editSaving ? "Saving…" : "Save changes"}</button>
                  <button style={S.btnGray} onClick={() => setEditing(null)} disabled={editSaving}>Close</button>
                </div>
              </div>
            );
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>💰 P&L Manager</div>
      </div>
      {canSeeBusinessPnl && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button style={tab === "today" ? S.btnBlue : S.btnGray} onClick={() => setTab("today")}>Today</button>
          <button style={tab === "projects" ? S.btnBlue : S.btnGray} onClick={() => setTab("projects")}>Projects</button>
          <button style={tab === "societies" ? S.btnBlue : S.btnGray} onClick={() => setTab("societies")}>Societies</button>
          <button style={tab === "entries" ? S.btnBlue : S.btnGray} onClick={() => setTab("entries")}>Entries</button>
          <button style={tab === "pnl" ? S.btnBlue : S.btnGray} onClick={() => setTab("pnl")}>P&L</button>
          <button style={tab === "settings" ? S.btnBlue : S.btnGray} onClick={() => setTab("settings")}>Settings</button>
          <button style={tab === "forecast" ? S.btnBlue : S.btnGray} onClick={() => setTab("forecast")}>Forecast</button>
        </div>
      )}
      {tab !== "settings" && msg && <div style={S.msg}>{msg}</div>}
      {canSeeBusinessPnl && tab === "forecast" ? (
        <PnlForecastTab />
      ) : canSeeBusinessPnl && tab === "settings" ? (
        <PnlSettings user={user} />
      ) : loading ? (
        <div style={{ color: "#7b82a0" }}>Loading…</div>
      ) : canSeeBusinessPnl && tab === "societies" ? (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <div style={{ color: "#c9cde0", fontSize: 13 }}>
              {socRows === null ? "Loading societies…" : (
                <>
                  <b>{societyView.rows.length}</b> {societyView.rows.length === 1 ? "society" : "societies"}
                  {" · "}Trial <b>{societyView.trial}</b>{" · "}Active <b>{societyView.active}</b>{" · "}Renewal overdue <b>{societyView.overdue}</b>
                  {" · "}Yearly subscriptions <b>{inr(societyView.yearlyTotal)}</b>
                  {societyView.rising > 0 && (<>{" · "}Price rise at renewal for <b>{societyView.rising}</b> {societyView.rising === 1 ? "society" : "societies"}, <b>+{inr(societyView.risingTotal)}</b> a year</>)}
                </>
              )}
            </div>
            <button style={S.btnGray} onClick={loadSocieties}>Refresh</button>
          </div>
          {socErr && <div style={S.msg}>{socErr}</div>}
          {socRows !== null && societyView.rows.length === 0 && !socErr ? (
            <div style={{ color: "#7b82a0", fontSize: 13 }}>No society has been set up yet. Societies appear here once Setup Wizard creates them.</div>
          ) : socRows !== null && (
            <div style={{ ...S.tableWrap, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={S.th}>Society ID</th>
                    <th style={S.th}>Society</th>
                    <th style={S.th}>Status</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Flats</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Monthly price</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Yearly amount</th>
                    <th style={S.th}>Billing starts</th>
                    <th style={S.th}>Renewal date</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Price at renewal</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Days left</th>
                  </tr>
                </thead>
                <tbody>
                  {societyView.rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ ...S.td, fontFamily: "monospace" }}>{r.id}</td>
                      <td style={S.td}>{r.name || "—"}</td>
                      <td style={S.td}><span style={chip(r.tone)}>{r.statusText}</span></td>
                      <td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{r.flats == null ? "—" : r.flats}</td>
                      <td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{r.monthly != null ? inr(r.monthly) : r.custom ? "Custom" : "—"}</td>
                      <td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{r.yearly != null ? inr(r.yearly) : r.custom ? "Custom" : "—"}</td>
                      <td style={S.td}>{fmtD(r.startIso)}</td>
                      <td style={S.td}>{fmtD(r.endIso)}</td>
                      <td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>
                        {r.renewal == null ? "—"
                          : r.change > 0 ? <span style={{ color: "#f0b429", fontWeight: 700 }}>{inr(r.renewal)} (+{inr(r.change)})</span>
                          : r.change < 0 ? <span style={{ color: "#4f8ef7" }}>{inr(r.renewal)} (-{inr(Math.abs(r.change))})</span>
                          : <span style={{ color: "#7b82a0" }}>{inr(r.renewal)} no change</span>}
                      </td>
                      <td style={{ ...S.td, fontFamily: "monospace", textAlign: "right", color: r.days !== null && r.days < 0 ? "#f0b429" : "#e8eaf0" }}>
                        {r.days === null ? "—" : r.days < 0 ? Math.abs(r.days) + " overdue" : r.days}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ color: "#7b82a0", fontSize: 12, marginTop: 8 }}>
            Read from what Setup Wizard saved. Subscription is yearly: billing starts the day after the 45-day trial ends and renews after one year. Monthly price is what the society agreed in Setup Wizard and yearly amount is that times 12. Price at renewal is what the society will pay at its renewal under the current Pricing Settings; until then it keeps its agreed price. Days left counts the trial days while on trial, then the days to renewal.
          </div>
        </>
      ) : canSeeBusinessPnl && tab === "projects" ? (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            <div>
              <label style={S.label}>Financial year</label>
              <select style={S.select} value={fy} onChange={(e) => setFy(e.target.value)}>
                {fyOptions.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                <option value="all">All time</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Month</label>
              <select style={S.select} value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="all">Whole year</option>
                {monthOrder.map((m, i) => <option key={m} value={m}>{MONTH_NAMES[i]}</option>)}
              </select>
            </div>
          </div>
          <div style={S.section}>Projects — {periodText}{month === "all" ? "" : ` · ${MONTH_NAMES[monthOrder.indexOf(month)]}`}</div>
          <div style={S.tableWrap}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={S.th}>Project</th><th style={S.th}>Income</th><th style={S.th}>Expenses</th>
                  <th style={S.th}>of which Google Cloud</th><th style={S.th}>Profit / Loss</th>
                </tr>
              </thead>
              <tbody>
                {projectRows.rows.map((r) => (
                  <tr key={r.project}>
                    <td style={S.td}>{r.project}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#3ecf8e" }}>{inr(r.income)}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#f87171" }}>{inr(r.expense)}</td>
                    <td style={{ ...S.td, fontFamily: "monospace" }}>{r.google ? inr(r.google) : "—"}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 700, color: r.profit >= 0 ? "#3ecf8e" : "#f87171" }}>{inr(r.profit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td style={S.td}>All projects</td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#3ecf8e" }}>{inr(projectRows.total.income)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#f87171" }}>{inr(projectRows.total.expense)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{projectRows.total.google ? inr(projectRows.total.google) : "—"}</td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: projectRows.total.profit >= 0 ? "#3ecf8e" : "#f87171" }}>{inr(projectRows.total.profit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={{ color: "#7b82a0", fontSize: 12, marginTop: 8 }}>
            Amounts are without GST. Owner's money is not counted. Company (common) holds costs that belong to the whole business, such as registration and the domain.
          </div>
        </>
      ) : canSeeBusinessPnl && tab === "entries" ? (
        <>
              <div style={S.section}>Add Entry</div>
              <div style={S.form}>
                <div style={S.formGrid}>
                  <div>
                    <label style={S.label}>Date</label>
                    <DateBox value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
                  </div>
                  <div>
                    <label style={S.label}>Type</label>
                    <select style={S.select} value={form.type} onChange={(e) => { const t = e.target.value; setForm({ ...form, type: t, paidFrom: PAID_FROM_BANK, category: t === "Transfer" || t === TOPUP_TYPE ? form.category : categoriesFor(t)[0] === "Subscription" ? "Subscription" : "Other" }); }}>
                      <option value="Income">Income (money received)</option>
                      <option value="Expense">Expense (money spent)</option>
                      <option value="Transfer">Owner's money / Transfer</option>
                      <option value={TOPUP_TYPE}>Google credit top-up (paid to Google in advance)</option>
                    </select>
                  </div>
                  {isTopup ? null : form.type !== "Transfer" ? (
                    <div>
                      <label style={S.label}>Category</label>
                      <select style={S.select} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, paidFrom: e.target.value === GOOGLE_CATEGORY ? form.paidFrom : form.paidFrom === PAID_FROM_CREDIT ? PAID_FROM_BANK : form.paidFrom })}>
                        {categoriesFor(form.type).map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label style={S.label}>Direction</label>
                      <select style={S.select} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                        <option value="In">Money put in by owner</option>
                        <option value="Out">Money taken out by owner</option>
                      </select>
                    </div>
                  )}
                  {!isGoogleForm && (
                    <div>
                      <label style={S.label}>Amount (₹)</label>
                      <input style={S.input} type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" />
                    </div>
                  )}
                  {!noGst && !isGoogleForm && (
                    <div>
                      <label style={S.label}>Project</label>
                      <select style={S.select} value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
                        {visibleProjects.map((n) => <option key={n}>{n}</option>)}
                      </select>
                    </div>
                  )}
                  {(form.type === "Expense" || isTopup) && (
                    <div>
                      <label style={S.label}>Paid from</label>
                      <select style={S.select} value={form.paidFrom} onChange={(e) => setForm({ ...form, paidFrom: e.target.value })}>
                        <option value={PAID_FROM_BANK}>Bank</option>
                        <option value={PAID_FROM_OWNER}>Owner's pocket</option>
                        {isGoogleForm && <option value={PAID_FROM_CREDIT}>Google credit</option>}
                      </select>
                    </div>
                  )}
                  {form.type === "Income" && (
                    <div>
                      <label style={S.label}>TDS deducted by customer (₹, optional)</label>
                      <input style={S.input} type="number" min="0" step="any" value={form.tdsAmount} onChange={(e) => setForm({ ...form, tdsAmount: e.target.value })} placeholder="0" />
                    </div>
                  )}
                  {!noGst && (
                    <>
                      <div>
                        <label style={S.label}>GST % (type the rate, e.g. 18)</label>
                        <input style={S.input} type="number" min="0" max="100" step="any" value={form.gstRate} onChange={(e) => setForm({ ...form, gstRate: e.target.value })} placeholder="0" />
                      </div>
                      {Number(form.gstRate) > 0 && (
                        <div>
                          <label style={S.label}>Amount typed is</label>
                          <select style={S.select} value={form.gstIncluded} onChange={(e) => setForm({ ...form, gstIncluded: e.target.value })}>
                            <option value="no">Without GST (add GST on top)</option>
                            <option value="yes">With GST included</option>
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  {showMode && (
                    <div>
                      <label style={S.label}>Payment mode</label>
                      <select style={S.select} value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>
                        {BANK_MODES.map((m) => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label style={S.label}>Customer / Vendor</label>
                    <input style={S.input} list="pnl-won-societies" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} placeholder={form.type === "Income" ? "Pick the society" : "Optional"} />
                    <datalist id="pnl-won-societies">
                      {form.type === "Income" && wonLeads.map((l) => <option key={l.id} value={l.societyName} />)}
                    </datalist>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={S.label}>Description</label>
                  <input style={S.input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Server hosting for July" />
                </div>
                {isGoogleForm && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={S.label}>Google Cloud bill: amount for each Google project (₹, leave empty for none)</label>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {links.map((l) => (
                        <div key={l.googleProject}>
                          <div style={{ fontSize: 12, color: "#7b82a0", marginBottom: 4, fontFamily: "monospace" }}>{l.googleProject} · {l.app}</div>
                          <input style={S.input} type="number" min="0" step="any" value={form.gcRows[l.googleProject] || ""}
                            onChange={(e) => setForm({ ...form, gcRows: { ...form.gcRows, [l.googleProject]: e.target.value } })} placeholder="0" />
                        </div>
                      ))}
                    </div>
                    {links.length === 0 && <div style={{ color: "#f0b429", fontSize: 13 }}>No Google projects are linked yet. Add them in Settings.</div>}
                  </div>
                )}
                {!noGst && !isGoogleForm && Number(form.gstRate) > 0 && Number(form.amount) > 0 && (() => {
                  const { base, gst } = splitGst(form.amount, form.gstRate, form.gstIncluded);
                  return (
                    <div style={{ color: "#7b82a0", fontSize: 13, marginBottom: 12 }}>
                      Amount without GST {inr(base)} + GST {inr(gst)} = Total {inr(base + gst)}
                    </div>
                  );
                })()}
                <button style={S.btnBlue} onClick={handleAdd} disabled={saving}>{saving ? "Saving…" : "Add Entry"}</button>
              </div>

          <div style={S.section}>Entries — {periodText}{month === "all" ? "" : ` · ${MONTH_NAMES[monthOrder.indexOf(month)]}`} · {shown.length} shown</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            <div>
              <label style={S.label}>Financial year</label>
              <select style={S.select} value={fy} onChange={(e) => setFy(e.target.value)}>
                {fyOptions.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                <option value="all">All time</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Month</label>
              <select style={S.select} value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="all">Whole year</option>
                {monthOrder.map((m, i) => <option key={m} value={m}>{MONTH_NAMES[i]}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Project</label>
              <select style={S.select} value={fProject} onChange={(e) => setFProject(e.target.value)}>
                <option value="all">All projects</option>
                {projectChoices.map((n) => <option key={n} value={n}>{n}</option>)}
                <option value={NO_PROJECT}>No project (Owner's money)</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Type</label>
              <select style={S.select} value={fType} onChange={(e) => setFType(e.target.value)}>
                <option value="all">All types</option>
                {TYPE_FILTERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Search</label>
              <input style={S.input} value={fText} onChange={(e) => setFText(e.target.value)} placeholder="Description, customer, category" />
            </div>
            {moreFilters && <button style={S.btnGray} onClick={() => { setFProject("all"); setFType("all"); setFText(""); }}>Clear filters</button>}
            <button style={S.btnBlue} onClick={() => handleDownload(shown, moreFilters)}>Download for Excel</button>
          </div>


          <div style={S.tableWrap}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th><th style={S.th}>Description</th><th style={S.th}>Party</th><th style={S.th}>Project</th>
                  <th style={S.th}>Category</th><th style={S.th}>Type</th><th style={S.th}>Mode</th><th style={S.th}>Paid from</th>
                  <th style={S.th}>Amount</th><th style={S.th}>GST</th><th style={S.th}>Total</th><th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {[...shown].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => {
                  const isReversal = !!e.reversalOf;
                  const isCancelled = cancelledIds.has(e.id);
                  const dim = isReversal || isCancelled;
                  const typeColor = e.type === "Income" ? "#3ecf8e" : e.type === "Expense" ? "#f87171" : "#4f8ef7";
                  const log = editLogList(e);
                  return (
                    <React.Fragment key={e.id}>
                      <tr style={dim ? { opacity: 0.5 } : undefined}>
                        <td style={S.td}>{fmtD(e.date)}</td>
                        <td style={{ ...S.td, textDecoration: isCancelled ? "line-through" : "none" }}>{e.description}</td>
                        <td style={S.td}>{e.party || "—"}</td>
                        <td style={S.td}>{e.project || "—"}</td>
                        <td style={S.td}>{e.category}</td>
                        <td style={{ ...S.td, color: typeColor }}>{typeLabelOf(e)}</td>
                        <td style={S.td}>{e.paymentMode || "—"}</td>
                        <td style={S.td}>{paidLabel(e) || "—"}</td>
                        <td style={{ ...S.td, fontFamily: "monospace" }}>{inr(e.amount)}{money(e.tdsAmount) ? <div style={{ color: "#7b82a0", fontSize: 11 }}>TDS {inr(e.tdsAmount)}</div> : null}</td>
                        <td style={{ ...S.td, fontFamily: "monospace" }}>{money(e.gstAmount) ? inr(e.gstAmount) : "—"}{money(e.gstRate) ? <span style={{ color: "#7b82a0", fontSize: 11 }}> ({money(e.gstRate)}%)</span> : null}</td>
                        <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 700 }}>{inr(money(e.amount) + money(e.gstAmount))}</td>
                        <td style={S.td}>
                          {isCancelled ? "Cancelled" : isReversal ? "Reversal" : (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <button style={S.btnGray} onClick={() => handleEditStart(e)}>Edit</button>
                              <button style={S.btnRed} onClick={() => handleCancel(e)}>Cancel entry</button>
                            </div>
                          )}
                          {log.length > 0 && (
                            <div style={{ marginTop: 6 }}>
                              <button style={{ ...S.btnGray, padding: "3px 8px", fontSize: 11 }} onClick={() => setOpenLog(openLog === e.id ? null : e.id)}>
                                Edited ({log.length}) {openLog === e.id ? "▲" : "▼"}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {editing && editing.id === e.id && (
                        <tr>
                          <td style={{ ...S.td, background: "#222638" }} colSpan={12}>{editPanel(e)}</td>
                        </tr>
                      )}
                      {openLog === e.id && log.length > 0 && (
                        <tr>
                          <td style={{ ...S.td, background: "#222638", color: "#a5b4fc", fontSize: 12 }} colSpan={12}>
                            {log.map((l, i) => (
                              <div key={i} style={{ marginBottom: 4 }}>{whenText(l.at)} · {l.by || "—"} · {describeChanges(l.changes)}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td style={S.td} colSpan={8}>Total income — shown above</td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#3ecf8e" }}>{inr(shownIncome.amount)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{inr(shownIncome.gst)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{inr(shownIncome.amount + shownIncome.gst)}</td>
                  <td style={S.td}></td>
                </tr>
                <tr style={{ fontWeight: 700 }}>
                  <td style={S.td} colSpan={8}>Total expenses — shown above</td>
                  <td style={{ ...S.td, fontFamily: "monospace", color: "#f87171" }}>{inr(shownExpense.amount)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{inr(shownExpense.gst)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{inr(shownExpense.amount + shownExpense.gst)}</td>
                  <td style={S.td}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : canSeeBusinessPnl && tab === "today" ? (
        <>
          <div style={S.metricRow}>
            <div style={S.metricCard("#3ecf8e")}>
              <div style={S.metricLbl}>Money in the bank</div>
              <div style={S.metricVal(today.bank >= 0 ? "#3ecf8e" : "#f87171")}>{inr(today.bank)}</div>
              <div style={S.metricSub}>Should match your bank balance (NEFT and UPI only)</div>
            </div>
            <div style={S.metricCard("#4f8ef7")}>
              <div style={S.metricLbl}>Total money you put in</div>
              <div style={S.metricVal("#4f8ef7")}>{inr(today.capital)}</div>
              <div style={S.metricSub}>Your capital, including what you paid from your pocket</div>
            </div>
            <div style={S.metricCard("#f0b429")}>
              <div style={S.metricLbl}>Google prepaid credit left</div>
              <div style={S.metricVal("#f0b429")}>{inr(today.credit)}</div>
              <div style={S.metricSub}>Not in the bank</div>
            </div>
          </div>
          {today.untagged > 0 && (
            <div style={{ ...S.msg, marginBottom: 16 }}>{today.untagged} expense entries have no "Paid from", so they are not counted here. Open the P&L tab to fix them.</div>
          )}
          <div style={S.section}>How your capital adds up</div>
          <div style={S.tableWrap}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr><td style={S.td}>Money put in the bank by you, less money you took out</td><td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{inr(today.capitalParts.ownerNet)}</td></tr>
                <tr><td style={S.td}>Expenses you paid from your pocket</td><td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{inr(today.capitalParts.expOwner)}</td></tr>
                <tr><td style={S.td}>Google credit you paid from your pocket</td><td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{inr(today.capitalParts.topOwner)}</td></tr>
                <tr style={{ fontWeight: 700 }}><td style={S.td}>Total money you put in</td><td style={{ ...S.td, fontFamily: "monospace", textAlign: "right" }}>{inr(today.capital)}</td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ color: "#7b82a0", fontSize: 12, marginTop: 8 }}>All time, from every entry. Cancelled entries are already netted out.</div>
        </>
      ) : (
        <>
          {canSeeBusinessPnl && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
              <div>
                <label style={S.label}>Financial year</label>
                <select style={S.select} value={fy} onChange={(e) => setFy(e.target.value)}>
                  {fyOptions.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                  <option value="all">All time</option>
                </select>
              </div>
              <div>
                <label style={S.label}>Month</label>
                <select style={S.select} value={month} onChange={(e) => setMonth(e.target.value)}>
                  <option value="all">Whole year</option>
                  {monthOrder.map((m, i) => <option key={m} value={m}>{MONTH_NAMES[i]}</option>)}
                </select>
              </div>
              <button style={S.btnBlue} onClick={() => handleDownload(filtered, false)}>Download for Excel</button>
            </div>
          )}

          <div style={S.metricRow}>
            {canSeeBusinessPnl && (
              <>
                <div style={S.metricCard("#3ecf8e")}>
                  <div style={S.metricLbl}>Income Received</div>
                  <div style={S.metricVal("#3ecf8e")}>{inr(totalIncome)}</div>
                  <div style={S.metricSub}>{periodText} · excluding GST</div>
                </div>
                <div style={S.metricCard("#f87171")}>
                  <div style={S.metricLbl}>Total Expenses</div>
                  <div style={S.metricVal("#f87171")}>{inr(totalExpense)}</div>
                  <div style={S.metricSub}>{periodText} · excluding GST</div>
                </div>
                <div style={S.metricCard(netProfit >= 0 ? "#3ecf8e" : "#f87171")}>
                  <div style={S.metricLbl}>Net Profit</div>
                  <div style={S.metricVal(netProfit >= 0 ? "#3ecf8e" : "#f87171")}>{inr(netProfit)}</div>
                  <div style={S.metricSub}>Income − Expenses</div>
                </div>
                <div style={S.metricCard("#4f8ef7")}>
                  <div style={S.metricLbl}>Owner's Money (In − Out)</div>
                  <div style={S.metricVal("#4f8ef7")}>{inr(ownerMoney)}</div>
                  <div style={S.metricSub}>Not counted in profit</div>
                </div>
                <div style={S.metricCard("#f0b429")}>
                  <div style={S.metricLbl}>GST Collected − GST Paid</div>
                  <div style={S.metricVal("#f0b429")}>{inr(gstCollected - gstPaid)}</div>
                  <div style={S.metricSub}>Collected {inr(gstCollected)} · Paid {inr(gstPaid)}</div>
                </div>
              </>
            )}
            <div style={S.metricCard("#a78bfa")}>
              <div style={S.metricLbl}>{seesAllLeads ? "Won Deals (Not Yet Received)" : "Your Won Deals"}</div>
              <div style={S.metricVal("#a78bfa")}>{inr(myWonRevenue)}</div>
              <div style={S.metricSub}>{wonLeads.length} won lead{wonLeads.length === 1 ? "" : "s"} · all time, not in income</div>
            </div>
          </div>

          {canSeeBusinessPnl && (
            <>
              <div style={S.section}>Income vs Expense — {fy === "all" ? "Last 12 Months" : `FY ${fyLabel(Number(fy))}`}</div>
              <div style={{ ...S.card, marginBottom: 20, height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2e3250" />
                    <XAxis dataKey="label" stroke="#7b82a0" fontSize={12} />
                    <YAxis stroke="#7b82a0" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#1a1d27", border: "1px solid #2e3250", color: "#e8eaf0" }} formatter={(v) => inr(v)} />
                    <Line type="monotone" dataKey="income" stroke="#3ecf8e" strokeWidth={2} name="Income" />
                    <Line type="monotone" dataKey="expense" stroke="#f87171" strokeWidth={2} name="Expense" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {expenseByCategory.length > 0 && (
                <>
                  <div style={S.section}>Expenses by Category — {periodText}</div>
                  <div style={{ ...S.card, marginBottom: 20, height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={expenseByCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(d) => d.name}>
                          {expenseByCategory.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#1a1d27", border: "1px solid #2e3250", color: "#e8eaf0" }} formatter={(v) => inr(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {untagged.length > 0 && (
                <div style={{ ...S.card, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: "#f0b429", marginBottom: 10 }}>
                    {untagged.length} old expense entries (copied from the old screen) have no "Paid from" yet. You confirmed they were paid from your own pocket, before the bank account was opened. They will count as money you put in, and they will not reduce the bank balance.
                  </div>
                  <button style={S.btnBlue} onClick={handleTag}>Mark them as paid from my pocket</button>
                </div>
              )}

              <div style={S.section}>Google Cloud cost (from Google)</div>
              <div style={{ ...S.card, marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: "#7b82a0", marginBottom: 10 }}>
                  Google's own billed cost is checked automatically when this screen opens. Only what is new is booked, from your Google credit, one expense per project under its app.
                </div>
                {gAuto && <div style={{ fontSize: 13, color: "#a5b4fc", marginBottom: 10 }}>{gAuto}</div>}
                {!gBook ? (
                  <button style={S.btnBlue} disabled={gBusy || links.length === 0} onClick={handleGoogleLook}>
                    {gBusy ? "Reading from Google…" : "Check again now"}
                  </button>
                ) : (
                  <>
                    <div style={S.tableWrap}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr><th style={S.th}>Google project</th><th style={S.th}>App</th><th style={S.th}>Google cost so far</th><th style={S.th}>Already booked</th><th style={S.th}>New to book</th></tr>
                        </thead>
                        <tbody>
                          {gBook.rows.map((r) => (
                            <tr key={r.googleProject}>
                              <td style={{ ...S.td, fontFamily: "monospace" }}>{r.googleProject}</td>
                              <td style={S.td}>{r.app}</td>
                              <td style={{ ...S.td, fontFamily: "monospace" }}>{r.known ? inr(r.cost) : "not available"}</td>
                              <td style={{ ...S.td, fontFamily: "monospace" }}>{inr(r.booked)}</td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 700 }}>{r.known ? inr(r.toBook) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
                      <div>
                        <label style={S.label}>Paid from</label>
                        <select style={S.select} value={gPaidFrom} onChange={(e) => setGPaidFrom(e.target.value)}>
                          <option value={PAID_FROM_BANK}>Bank</option>
                          <option value={PAID_FROM_OWNER}>Owner's pocket</option>
                          <option value={PAID_FROM_CREDIT}>Google credit</option>
                        </select>
                      </div>
                      <button style={S.btnBlue} disabled={gBusy} onClick={handleGoogleBook}>{gBusy ? "Saving…" : "Book the new cost"}</button>
                      <button style={S.btnGray} disabled={gBusy} onClick={() => setGBook(null)}>Close</button>
                    </div>
                    <div style={{ color: "#7b82a0", fontSize: 12, marginTop: 8 }}>
                      Dollars changed to rupees at today's rate (₹{gBook.rate ? gBook.rate.toFixed(2) : "—"} per dollar). The cost is Google's billed amount without GST.
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          <div style={S.section}>Won Deals by Lead {seesAllLeads ? "(All)" : "(Yours)"} — Expected, Not Yet Received</div>
          <div style={S.tableWrap}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={S.th}>Society</th><th style={S.th}>Stage</th><th style={S.th}>Subscription (mo.)</th><th style={S.th}>Onboarding Fee</th><th style={S.th}>Total Value</th>{canSeeBusinessPnl && (<><th style={S.th}>Received</th><th style={S.th}>Balance</th></>)}</tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td style={S.td}>{l.societyName}</td>
                    <td style={S.td}>{l.stage}</td>
                    <td style={S.td}>{inr(l.subscriptionValue)}</td>
                    <td style={S.td}>{inr(l.onboardingFee)}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: "#a78bfa" }}>{inr(leadRevenue(l))}</td>
                    {canSeeBusinessPnl && (() => {
                      const got = receivedByParty[(l.societyName || "").trim().toLowerCase()] || 0;
                      return l.stage === "Won" ? (
                        <>
                          <td style={{ ...S.td, color: "#3ecf8e" }}>{inr(got)}</td>
                          <td style={{ ...S.td, fontWeight: 700, color: leadRevenue(l) - got > 0 ? "#f0b429" : "#3ecf8e" }}>{inr(leadRevenue(l) - got)}</td>
                        </>
                      ) : (<><td style={S.td}>—</td><td style={S.td}>—</td></>);
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}